import { useCallback, useEffect, useRef, useState } from 'react';
import { usePasswordlessAuth } from '../../common/hooks/usePasswordlessAuth';
import { useAuthTokenSuccess } from '../../common/hooks/useAuthTokenSuccess';
import { useQueryParam } from '../../../utils/hooks/useQueryParam';
import { config } from '../../../config/config';
import { useSMLTheme } from '../../music24/theme/useSMLTheme';
import { useTurnstile } from './turnstile/useTurnstile';
import { authErrorKind } from './components/authErrorKind';
import { readStoredCodeStep, RESEND_WAIT_SECONDS, storeCodeStep } from './authCodeStepStorage';

const CODE_LENGTH = 6;

export function useAuthFormPasswordless() {
  const emailParam = useQueryParam('email', null, '');
  const autoSendDone = useRef(false);
  // A reload or Back on the code step comes back to it (QA 2026-10-28, brk-6). A link with
  // `?email=` starts its own send instead.
  const [restored] = useState(() => (emailParam === '' ? readStoredCodeStep() : null));

  const [emailInput, setEmailInput] = useState(emailParam || restored?.email || '');
  const [code, setCode] = useState('');
  // When the code on screen went out (ms), for the resend wait. Null: no wait.
  const [codeSentAt, setCodeSentAt] = useState<number | null>(restored?.sentAt ?? null);
  // One code check at a time, and never the same refused code twice: a double click on Continue
  // or a sixth digit and a click spent two of the five tries of a code (QA brk-15).
  const isVerifyingRef = useRef(false);
  const [refusedCode, setRefusedCode] = useState<string | null>(null);

  const onAuthToken = useAuthTokenSuccess();
  const { theme } = useSMLTheme();
  // A new token ends a refused check: the error goes as soon as the box reports success. Refs,
  // because the auth hook below needs the token getter first.
  const errorRef = useRef<Error | null>(null);
  const clearErrorRef = useRef<() => void>(() => undefined);
  const onTurnstileToken = useCallback(() => {
    if (authErrorKind(errorRef.current) === 'turnstile') {
      clearErrorRef.current();
    }
  }, []);
  // The hidden bot check of the two calls that send a login email. Only this page loads it.
  const turnstile = useTurnstile({ siteKey: config.turnstileSiteKey, theme, onToken: onTurnstileToken });

  const passwordlessAuth = usePasswordlessAuth({
    getTurnstileToken: turnstile.takeToken,
    // sign_up_finished / log_in_finished segmentation is handled inside
    // usePasswordlessAuth (SML-only), so this consumer only forwards the token.
    onSuccess: (token: string) => {
      storeCodeStep(null);
      onAuthToken(token);
    },
    onError: (error) => {
      console.error('Auth error:', error);
    },
  });

  errorRef.current = passwordlessAuth.error;
  clearErrorRef.current = passwordlessAuth.clearError;
  const { needsInteraction } = turnstile;
  const { showCodeStep } = passwordlessAuth;

  /** Keeps the code step of this address for a reload, and starts the resend wait from `sentAt`. */
  const keepCodeStep = useCallback((address: string, sentAt: number) => {
    storeCodeStep({ email: address, sentAt });
    setCodeSentAt(sentAt);
  }, []);

  useEffect(() => {
    if (restored !== null) {
      showCodeStep(restored.email);
      return;
    }
    if (emailParam !== '' && !autoSendDone.current) {
      autoSendDone.current = true;
      passwordlessAuth
        .requestCode(emailParam)
        .then(() => keepCodeStep(emailParam, Date.now()))
        .catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Design u2: the disposable-email field error clears the moment the person edits the address —
  // it is a stale answer to the address that was just there, not a running validation of the new
  // one. `clearError` is a no-op when there is nothing to clear, so this costs nothing on every
  // keystroke while there is no error showing.
  const setEmailInputAndClearError = useCallback(
    (value: string) => {
      setEmailInput(value);
      passwordlessAuth.clearError();
    },
    [passwordlessAuth]
  );

  const submitEmail = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      // The box waits for a tick: a send now would only wait 30 s and fail.
      if (needsInteraction) {
        return;
      }
      const address = emailInput.trim();
      if (address !== '') {
        passwordlessAuth
          .requestCode(address)
          .then(() => keepCodeStep(address, Date.now()))
          .catch(console.error);
      }
    },
    [emailInput, keepCodeStep, needsInteraction, passwordlessAuth]
  );

  /** "I already have a code": the code step with no new send, and no resend wait. */
  const openCodeStep = useCallback(() => {
    const address = emailInput.trim();
    if (address === '') {
      return;
    }
    const stored = readStoredCodeStep();
    keepCodeStep(address, stored?.email === address ? stored.sentAt : Date.now() - RESEND_WAIT_SECONDS * 1000);
    setCode('');
    showCodeStep(address);
  }, [emailInput, keepCodeStep, showCodeStep]);

  /** Checks a code: the one in the field, or `value` (the sixth digit, typed or pasted). */
  const verify = useCallback(
    (event?: React.SyntheticEvent | null, value?: string) => {
      event?.preventDefault();
      const candidate = value ?? code;
      if (candidate.length !== CODE_LENGTH || isVerifyingRef.current || candidate === refusedCode) {
        return;
      }
      isVerifyingRef.current = true;
      passwordlessAuth
        .verifyCode(candidate)
        .catch((error: unknown) => {
          if (authErrorKind(error as Error) === 'badCode') {
            setRefusedCode(candidate);
          }
          console.error(error);
        })
        .finally(() => {
          isVerifyingRef.current = false;
        });
    },
    [code, passwordlessAuth, refusedCode]
  );

  const resend = useCallback(() => {
    if (needsInteraction) {
      return;
    }
    passwordlessAuth
      .resendCode()
      .then(() => {
        setRefusedCode(null);
        keepCodeStep(passwordlessAuth.email, Date.now());
      })
      .catch(console.error);
  }, [keepCodeStep, needsInteraction, passwordlessAuth]);

  const goBackToEmail = useCallback(() => {
    storeCodeStep(null);
    setCode('');
    setRefusedCode(null);
    passwordlessAuth.goBackToEmail();
  }, [passwordlessAuth]);

  return {
    step: passwordlessAuth.step,
    email: passwordlessAuth.email,
    emailInput,
    setEmailInput: setEmailInputAndClearError,
    code,
    setCode,
    submitEmail,
    openCodeStep,
    verify,
    /** True when the code in the field was refused: Continue waits for a new code. */
    isRefusedCode: refusedCode !== null && code === refusedCode,
    resend,
    /** When the code on screen went out (ms), or null when the page does not know. */
    codeSentAt,
    goBackToEmail,
    error: passwordlessAuth.error,
    isLoading: passwordlessAuth.isRequestingCode || passwordlessAuth.isVerifyingCode,
    isResending: passwordlessAuth.isResendingCode,
    turnstile: { slotRef: turnstile.slotRef, isVisible: turnstile.isVisible, needsInteraction },
  };
}
